import {
	DateString,
	Icon,
	PersonAvatar,
	ListItem,
	Space,
	NoResults
} from "client/Components";
import {
	ComputedDisplayNameResult,
	getComputedDisplayName
} from "client/Features/AddressBook/common";
import { showContactDialog } from "client/Features/AddressBook/ContactDialog";
import { ContactSource, getWorkspaceContactIdFromUserId } from "client/Api/Email/contacts";
import { getUserProfileImageUrl } from "client/Api/UserProfile/service";
import { MakeBirthdayWishDialog } from "client/Features/Users/Birthday/MakeBirthdayWishDialog";
import { getClientId, ObjectWithClientId } from "client/Misc/clientId";
import {
	ensureNotNil,
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { BirthdayListItem } from "client/Api/Workspace/LiveInformation/Birthday/birthdayLiveInformation";
import { LiveTileContent } from "../LiveTileContent";
import styles from "./BirthdayList.module.less";
import { getIsToday } from "client/DateAndTime/dateTimeUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import { useToday } from "client/Hooks";
import { convertToDateTime, parseDtoDateOnlyIgnoringOffset } from "client/DateAndTime/dateUtils";

export type BirthdaysListProps = {
	pageNumber: number;
	isLoading: boolean;
	items: readonly ObjectWithClientId<BirthdayListItem>[];
	hasMoreItems: boolean;
	onLoadMore: () => void;
	onAfterWishSent: (item: ObjectWithClientId<BirthdayListItem>) => void;
	timezone: TimeZone;
	onListItemClick(): void;
};

export const BirthdayList = (props: BirthdaysListProps): ReactElement => {
	const {
		pageNumber,
		isLoading,
		items,
		hasMoreItems,
		onLoadMore,
		onAfterWishSent,
		timezone,
		onListItemClick
	} = props;

	if (items.length === 0
		&& pageNumber === 0
		&& !isLoading
	) {
		return (
			<NoResults
				trackingId="no-items"
				icon="cake-candles"
				title={localize("_NoUpcomingBirthdays")}
				message={localize("_ThereAreNoUpcomingBirthdaysToShowRightNow")}
				height="fill"
			/>
		);
	}

	return (
		<>
			<LiveTileContent.List>
				{items.map(x => (
					<Birthday
						key={getClientId(x)}
						item={x}
						onAfterWishSent={() => onAfterWishSent(x)}
						timezone={timezone}
						onClick={onListItemClick}
					/>
				))}
			</LiveTileContent.List>

			{hasMoreItems && (
				<LiveTileContent.LoadMoreButton onClick={onLoadMore} />
			)}
		</>
	);
};
BirthdayList.displayName = "BirthdayList";

export type BirthdayProps = {
	item: ObjectWithClientId<BirthdayListItem>;
	onAfterWishSent: () => void;
	timezone: TimeZone;
	onClick(): void;
};

export const Birthday = (props: BirthdayProps): ReactElement => {
	const {
		item,
		onAfterWishSent,
		timezone,
		onClick
	} = props;

	const [isWishDialogOpen, setIsWishDialogOpen] = useState<boolean>(false);

	const { firstName, lastName } = item;

	const today: Date = useToday({ timezone });

	const upcomingBirthday = useMemo(
		(): Date => {
			return convertToDateTime(
				ensureNotNil(
					parseDtoDateOnlyIgnoringOffset(item.upcomingBirthday)
				),
				timezone
			);
		},
		[item.upcomingBirthday, timezone]
	);

	const isToday = useMemo(
		(): boolean => {
			return getIsToday(
				upcomingBirthday,
				timezone,
				today
			);
		},
		[
			timezone,
			today,
			upcomingBirthday
		]
	);

	const { computedDisplayName, initials } = useMemo(
		(): ComputedDisplayNameResult => {
			return getComputedDisplayName({
				firstName,
				lastName
			});
		},
		[firstName, lastName]
	);

	const openContactHandler = useCallback(
		(): void => {
			onClick();
			showContactDialog({
				initialView: {
					kind: "details",
					contactId: getWorkspaceContactIdFromUserId(item.userId)
				},
				source: ContactSource.Workspace,
			});
		},
		[
			item.userId,
			onClick
		]
	);

	const wishSendHandler = useCallback(
		(): void => {
			setIsWishDialogOpen(false);
			onAfterWishSent();
		},
		[onAfterWishSent]
	);

	const canSendWish: boolean = isToday
		&& item.canMakeWish
		&& item.userId !== userConfiguration().currentUserIdentity
			.userId;

	return (
		<ListItem
			trackingId={generateTrackingId("birthday", `|${item.userId}`)}
			noMobileBorderBottom
		>
			<ListItem.Content
				onClick={openContactHandler}
				thumbnail={
					<PersonAvatar
						size="small"
						initials={initials}
						imageSrc={
							isNotNil(item.avatarId)
								? getUserProfileImageUrl(item.avatarId)
								: undefined
						}
					/>
				}
			>
				<ListItem.Title trackingId="title">
					{computedDisplayName}
					{isToday && (
						<>
							<Space />
							<Icon
								value={{
									kind: "svg",
									name: "party-popper"
								}}
								size="small"
							/>
						</>
					)}
				</ListItem.Title>
				<ListItem.SubTitle>
					<span className={isToday ? styles.info_today : undefined}>
						<DateString
							value={upcomingBirthday}
							format="RelativeDay"
							timezone={timezone}
						/>
					</span>
				</ListItem.SubTitle>
			</ListItem.Content>

			{canSendWish && (
				<ListItem.Action
					icon="paper-plane"
					onClick={() => setIsWishDialogOpen(true)}
					trackingId="send-wish"
				/>
			)}

			{isWishDialogOpen && (
				<MakeBirthdayWishDialog
					firstName={firstName}
					lastName={lastName}
					userId={item.userId}
					onClose={() => setIsWishDialogOpen(false)}
					onSend={wishSendHandler}
				/>
			)}
		</ListItem>
	);
};
Birthday.displayName = "Birtday";
